import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { ICON_MAP } from '../../utils/iconMap';

export default function MenuItemModal({ isOpen, onClose, item, group, onSave }) {
  const supabase = useSupabaseClient();
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: '',
    path: '',
    icon: '',
    tooltip: '',
  });

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || '',
        path: item.path || '',
        icon: item.icon || '',
        tooltip: item.tooltip || '',
      });
    } else {
      setFormData({
        name: '',
        path: '',
        icon: '',
        tooltip: '',
      });
    }
  }, [item]);

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.path || !formData.icon) {
        toast({
          title: 'Validation Error',
          description: 'Name, path, and icon are required',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      if (!group?.id) {
        toast({
          title: 'Error',
          description: 'Menu group is required',
          status: 'error',
          duration: 5000,
        });
        return;
      }

      const { data: lastItem } = await supabase
        .from('menu_items')
        .select('display_order')
        .eq('menu_group_id', group.id)
        .order('display_order', { ascending: false })
        .limit(1)
        .single();

      const newDisplayOrder = lastItem ? lastItem.display_order + 10 : 10;

      const { error } = await supabase
        .from('menu_items')
        .upsert({
          id: item?.id,
          name: formData.name,
          path: formData.path,
          icon: formData.icon,
          tooltip: formData.tooltip,
          menu_group_id: group.id,
          display_order: item ? item.display_order : newDisplayOrder,
        });

      if (error) throw error;

      toast({
        title: 'Success',
        description: `Menu item ${item ? 'updated' : 'created'} successfully`,
        status: 'success',
        duration: 3000,
      });

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving menu item:', error);
      toast({
        title: 'Error saving menu item',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {item ? 'Edit Menu Item' : `Add Menu Item to ${group?.name}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Item Name</FormLabel>
              <FormHelperText mb={2}>Enter a clear, concise name for this menu item</FormHelperText>
              <Input
                placeholder="e.g., Send Message, View History"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Navigation Path</FormLabel>
              <FormHelperText mb={2}>Specify the URL path for this menu item (e.g., /messages/send)</FormHelperText>
              <Input
                placeholder="/path/to/page"
                value={formData.path}
                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Icon</FormLabel>
              <FormHelperText mb={2}>Select an icon to represent this menu item</FormHelperText>
              <Select
                placeholder="Choose an icon"
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {Object.keys(ICON_MAP).map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Tooltip</FormLabel>
              <FormHelperText mb={2}>Add a helpful tooltip that appears when users hover over this item</FormHelperText>
              <Input
                placeholder="e.g., Send a new message to members"
                value={formData.tooltip}
                onChange={(e) => setFormData({ ...formData, tooltip: e.target.value })}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
